import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AppointmentService } from '@services/appointment.service';
import { AppointmentStore } from '@services/appointment.store';
import { GiftCardStore } from '@services/gift-card.store';
import { I18nService } from '@services/i18n.service';
import { filter, takeUntil } from 'rxjs';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzModalModule } from 'ng-zorro-antd/modal';

import { languages } from 'src/app/config/languages';
import { getHeaderContentType, hasRouteDataFlag, isDigitalServiceMenuMode } from 'src/app/lib/content-type.util';
import { SubscribedComponent } from 'src/app/lib/subscribed.component';
import { watchEntity } from 'src/app/lib/entity-watcher.util';
import { NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LocationService } from '@services/location.service';
import { GiftCardContentComponent } from '../gift-card-content/gift-card-content.component';
import { CartContentComponent } from '../cart-content/cart-content.component';
import { Location } from 'src/app/models/location.model';

@Component({
  selector: 'app-header-actions',
  templateUrl: './header-actions.component.html',
  styleUrl: './header-actions.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    NzModalModule,
    NzIconModule,
    NzCardModule,
    NzDropdownModule,
    NzMenuModule,
    GiftCardContentComponent,
    CartContentComponent,
  ],
})
export class HeaderActionsComponent extends SubscribedComponent implements OnInit {
  languageModalVisible = false;
  cartModalVisible = false;
  animateCartBadge = false;
  animateCartBadgeReverse = false;
  languages = languages;
  location: Location;
  currentLang: string;
  appointmentCartItemsCount = 0;
  isDigitalServiceMenuMode = false;
  hideSider = false;
  private hasInitializedCartCount = false;
  private hasInitializedGiftCardInCart = false;
  private hasGiftCardInCart = false;
  private cartBadgeAnimationTimeoutId?: ReturnType<typeof setTimeout>;
  contentType: 'cart' | 'gift-card' = 'cart';

  get currentLanguageName(): string {
    return this.languages.find((language) => language.code === this.currentLang)?.name ?? '';
  }

  get cartItemsCount(): number {
    return this.contentType === 'gift-card' ? (this.hasGiftCardInCart ? 1 : 0) : this.appointmentCartItemsCount;
  }

  constructor(
    private ref: ChangeDetectorRef,
    private i18nService: I18nService,
    private appointmentStore: AppointmentStore,
    private appointmentService: AppointmentService,
    private locationService: LocationService,
    private giftCardStore: GiftCardStore,
    private router: Router,
    private readonly destroyRef: DestroyRef,
  ) {
    super();
  }

  ngOnInit() {
    this.currentLang = this.i18nService.language;

    this.syncLocationState(this.locationService.getLocation());
    this.updateCartContentType();

    this.handleNavigationEnd();

    this.trackAppointmentUpdates();

    this.locationService.locationChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((location) => {
      this.syncLocationState(location);
    });

    this.trackGiftCardUpdates();
  }

  private trackAppointmentUpdates() {
    this.appointmentStore
      .watchAppointment()
      .pipe(
        filter((appointment) => appointment !== undefined),
        takeUntil(this.destroyed$),
      )
      .subscribe((appointment) => {
        const nextCartItemsCount = appointment ? this.appointmentService.getStandaloneItems(appointment).length : 0;

        if (this.hasInitializedCartCount && nextCartItemsCount !== this.appointmentCartItemsCount) {
          this.triggerCartBadgeAnimation(nextCartItemsCount < this.appointmentCartItemsCount);
        }

        this.appointmentCartItemsCount = nextCartItemsCount;
        this.hasInitializedCartCount = true;
        this.ref.markForCheck();
      });
  }

  private trackGiftCardUpdates() {
    watchEntity(this.giftCardStore.watchGiftCard(), this.destroyed$, (giftCard) => {
      const nextHasGiftCardInCart = !!giftCard?.value;

      if (this.hasInitializedGiftCardInCart && nextHasGiftCardInCart !== this.hasGiftCardInCart) {
        this.triggerCartBadgeAnimation(!nextHasGiftCardInCart);
      }

      this.hasGiftCardInCart = nextHasGiftCardInCart;
      this.hasInitializedGiftCardInCart = true;
      this.ref.markForCheck();
    });
  }

  private handleNavigationEnd() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.updateCartContentType());
  }

  override ngOnDestroy(): void {
    clearTimeout(this.cartBadgeAnimationTimeoutId);
    super.ngOnDestroy();
  }

  onShowLanguageModal(): void {
    this.languageModalVisible = true;
  }

  onHideLanguageModal(): void {
    this.languageModalVisible = false;
  }

  onChangeLanguage(lang: string): void {
    this.i18nService.changeLanguage(lang);
    this.currentLang = lang;
    this.languageModalVisible = false;
    this.ref.markForCheck();
  }

  onShowCartModal(): void {
    this.cartModalVisible = true;
  }

  onHideCartModal(): void {
    this.cartModalVisible = false;
    this.ref.markForCheck();
  }

  private triggerCartBadgeAnimation(reverse = false): void {
    this.animateCartBadge = false;
    this.animateCartBadgeReverse = reverse;
    clearTimeout(this.cartBadgeAnimationTimeoutId);

    this.cartBadgeAnimationTimeoutId = setTimeout(() => {
      this.animateCartBadge = true;
      this.ref.markForCheck();

      this.cartBadgeAnimationTimeoutId = setTimeout(() => {
        this.animateCartBadge = false;
        this.animateCartBadgeReverse = false;
        this.ref.markForCheck();
      }, 900);
    }, 0);
  }

  private updateCartContentType(): void {
    this.hideSider = hasRouteDataFlag(this.router.routerState.snapshot.root, 'hideSider');
    this.contentType = getHeaderContentType(this.router.routerState.snapshot.root);
  }

  private syncLocationState(location: Location): void {
    this.location = location;
    this.isDigitalServiceMenuMode = isDigitalServiceMenuMode(location);
    this.updateCartContentType();
    this.ref.markForCheck();
  }
}
